import {
  PageHeaderMenuItem,
  RenderFourthLevelMenuItemProps,
} from '@/components/PageHeader/PageHeader.model'
import { fetchAccessibleProductLink } from '@/utils/products/global-element-product-access-check'
import { renderProductDocumentPage } from '@/components/PageHeader/RenderProductDocumentPage'
import styles from '@/components/PageHeader/styles/pageHeader.module.scss'

/**
 * Renders a fourth-level menu item based on the provided properties.
 *
 * @param {RenderFourthLevelMenuItemProps} props - The properties required to render the menu item.
 * @param {PageHeaderMenuItem} props.fourthLevelMenuItem - The menu item to be rendered.
 * @param {number} props.fourthLevelItemIndex - The index of the menu item in the fourth-level menu.
 * @param {any} props.allProductVersionsInfo - Information about all product versions.
 * @param {string} props.countryCode - The country code for localization purposes.
 * @param {boolean} props.isElavon - A flag indicating whether the current context is for Elavon.
 *
 * @returns {JSX.Element | undefined} The rendered menu item as a JSX element, or `undefined` if no valid link is available.
 */
export function renderFourthLevelMenuItem({
  fourthLevelMenuItem,
  fourthLevelItemIndex,
  allProductVersionsInfo,
  countryCode,
  isElavon,
}: RenderFourthLevelMenuItemProps): JSX.Element | undefined {
  const subChild = fourthLevelMenuItem?.url?.split('/')
  const subChildPathDepth = subChild?.length ?? 0
  const subChildVersion = subChild ? subChild[subChild.length - 1] : ''
  const subChildProductSlug = subChild ? subChild[subChild.length - 2] : ''
  let subChildProductLink = fourthLevelMenuItem?.url

  if (!isElavon) {
    subChildProductLink = getSubChildProductLink({
      fourthLevelMenuItem,
      subChildPathDepth,
      subChildVersion,
      subChildProductSlug,
      allProductVersionsInfo,
      countryCode,
    })
  }

  return subChildProductLink
    ? renderMenuItem(
        fourthLevelMenuItem,
        fourthLevelItemIndex,
        subChildProductLink
      )
    : undefined
}

/**
 * Generates a link for a sub-child product based on the provided parameters.
 *
 * @param params - An object containing the following properties:
 *   @param params.fourthLevelMenuItem - The menu item representing the fourth level in the hierarchy.
 *   @param params.subChildPathDepth - The depth of the sub-child path in the hierarchy.
 *   @param params.subChildVersion - The version of the sub-child product.
 *   @param params.subChildProductSlug - The slug identifier for the sub-child product.
 *   @param params.allProductVersionsInfo - Information about all product versions.
 *   @param params.countryCode - The country code for localization purposes.
 *
 * @returns A string representing the accessible product link if the conditions are met,
 *          or the URL of the fourth-level menu item. Returns `undefined` if no URL is available.
 */
function getSubChildProductLink(params: {
  fourthLevelMenuItem: PageHeaderMenuItem
  subChildPathDepth: number
  subChildVersion: string
  subChildProductSlug: string
  allProductVersionsInfo: any
  countryCode: string
}): string | undefined {
  const {
    fourthLevelMenuItem,
    subChildPathDepth,
    subChildVersion,
    subChildProductSlug,
    allProductVersionsInfo,
    countryCode,
  } = params

  if (fourthLevelMenuItem?.value && subChildPathDepth - 1 > 2) {
    return fetchAccessibleProductLink(
      subChildVersion,
      subChildProductSlug,
      allProductVersionsInfo,
      countryCode
    )
  }
  return fourthLevelMenuItem?.url
}

/**
 * Renders a fourth-level menu item as a list item (`<li>`) element.
 *
 * @param fourthLevelMenuItem - The menu item object containing details for the fourth-level menu.
 * @param fourthLevelItemIndex - The index of the fourth-level menu item in the list.
 * @param subChildProductLink - The URL link associated with the menu item.
 * @returns A JSX element representing the fourth-level menu item, including its child pages if present.
 *
 * The function generates a nested structure of `<li>` and `<ul>` elements to represent
 * the menu hierarchy. If the `fourthLevelMenuItem` has child pages, they are rendered
 * as a nested list. Each menu item includes a link (`<a>`) with the provided `subChildProductLink`.
 */
function renderMenuItem(
  fourthLevelMenuItem: PageHeaderMenuItem,
  fourthLevelItemIndex: number,
  subChildProductLink: string
): JSX.Element {
  return (
    <li className={styles.greatGrandSubMenu} key={fourthLevelItemIndex}>
      <a className={styles.nested} href={`${subChildProductLink}` || '#'}>
        {fourthLevelMenuItem.value}
      </a>

      {fourthLevelMenuItem?.childPages && (
        <ul
          className={`${styles.childPages} ${styles.level4} ${styles.noArrow}`}
        >
          <li
            className={styles.greatGrandSubMenu}
            key={`productOverview-${fourthLevelItemIndex}`}
          >
            <a className={styles.nested} href={`${subChildProductLink}` || '#'}>
              Product Overview
            </a>
          </li>
          {fourthLevelMenuItem?.childPages?.map(
            (
              productDocumentPage: PageHeaderMenuItem,
              productDocumentPageIndex: number
            ) =>
              renderProductDocumentPage(
                productDocumentPage,
                productDocumentPageIndex
              )
          )}
        </ul>
      )}
    </li>
  )
}
